import { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../services/apiClient';

const emptyCategory = { name: '', description: '', sort_order: 0, is_active: true };
const emptyType = { name: '', category_id: '', description: '', sort_order: 0, is_active: true };

const AdminPropertyTypes = ({ token }) => {
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [catForm, setCatForm] = useState(emptyCategory);
  const [typeForm, setTypeForm] = useState(emptyType);
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const categoryMap = useMemo(
    () => Object.fromEntries(categories.map((c) => [String(c.id), c.name])),
    [categories]
  );

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [catRes, typeRes] = await Promise.all([
        apiGet('/api/admin/property-categories', token),
        apiGet('/api/admin/property-types', token),
      ]);
      setCategories(catRes || []);
      setTypes(typeRes || []);
    } catch (err) {
      setError(err.message || 'Failed to load property masters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetCatForm = () => {
    setCatForm(emptyCategory);
    setEditingCatId(null);
  };
  const resetTypeForm = () => {
    setTypeForm(emptyType);
    setEditingTypeId(null);
  };

  const saveCategory = async () => {
    if (!catForm.name) return;
    setSaving(true);
    setError('');
    try {
      if (editingCatId) {
        await apiPut(`/api/admin/property-categories/${editingCatId}`, catForm, token);
      } else {
        await apiPost('/api/admin/property-categories', catForm, token);
      }
      await load();
      resetCatForm();
    } catch (err) {
      setError(err.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const saveType = async () => {
    if (!typeForm.name) return;
    setSaving(true);
    setError('');
    const payload = { ...typeForm, category_id: typeForm.category_id || null };
    try {
      if (editingTypeId) {
        await apiPut(`/api/admin/property-types/${editingTypeId}`, payload, token);
      } else {
        await apiPost('/api/admin/property-types', payload, token);
      }
      await load();
      resetTypeForm();
    } catch (err) {
      setError(err.message || 'Failed to save type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Properties</p>
          <h1 className="admin-page-title text-xl font-semibold text-slate-900">Categories / Types</h1>
        </div>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="admin-card bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Categories</h2>
            <span className="text-xs text-slate-500">{categories.length} items</span>
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label text-sm">Name</label>
              <input
                className="form-control"
                value={catForm.name}
                onChange={(e) => setCatForm((v) => ({ ...v, name: e.target.value }))}
                placeholder="Villa"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label text-sm">Sort order</label>
              <input
                type="number"
                className="form-control"
                value={catForm.sort_order}
                onChange={(e) => setCatForm((v) => ({ ...v, sort_order: Number(e.target.value) }))}
              />
            </div>
            <div className="col-12">
              <label className="form-label text-sm">Description</label>
              <textarea
                className="form-control"
                rows={2}
                value={catForm.description}
                onChange={(e) => setCatForm((v) => ({ ...v, description: e.target.value }))}
              />
            </div>
            <div className="col-md-4 d-flex align-items-center gap-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="cat-active"
                checked={catForm.is_active}
                onChange={(e) => setCatForm((v) => ({ ...v, is_active: e.target.checked }))}
              />
              <label htmlFor="cat-active" className="form-check-label text-sm">
                Active
              </label>
            </div>
            <div className="col-12 d-flex gap-2">
              <button className="btn btn-primary" disabled={saving} onClick={saveCategory}>
                {saving && editingCatId ? 'Updating...' : saving ? 'Saving...' : editingCatId ? 'Update' : 'Add'}
              </button>
              {editingCatId && (
                <button className="btn btn-outline-secondary" onClick={resetCatForm}>
                  Cancel
                </button>
              )}
            </div>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Sort</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && !categories.length && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted">
                      No categories yet
                    </td>
                  </tr>
                )}
                {categories.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="fw-semibold">{c.name}</div>
                      <div className="text-muted small">{c.description}</div>
                    </td>
                    <td>{c.sort_order}</td>
                    <td>
                      <span className={`badge ${c.is_active ? 'bg-success-subtle text-success' : 'bg-secondary'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          setEditingCatId(c.id);
                          setCatForm({
                            name: c.name || '',
                            description: c.description || '',
                            sort_order: c.sort_order || 0,
                            is_active: c.is_active ?? true,
                          });
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-card bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Types</h2>
            <span className="text-xs text-slate-500">{types.length} items</span>
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label text-sm">Name</label>
              <input
                className="form-control"
                value={typeForm.name}
                onChange={(e) => setTypeForm((v) => ({ ...v, name: e.target.value }))}
                placeholder="Luxury villa"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label text-sm">Category</label>
              <select
                className="form-select"
                value={typeForm.category_id}
                onChange={(e) => setTypeForm((v) => ({ ...v, category_id: e.target.value }))}
              >
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label text-sm">Sort order</label>
              <input
                type="number"
                className="form-control"
                value={typeForm.sort_order}
                onChange={(e) => setTypeForm((v) => ({ ...v, sort_order: Number(e.target.value) }))}
              />
            </div>
            <div className="col-md-8">
              <label className="form-label text-sm">Description</label>
              <input
                className="form-control"
                value={typeForm.description}
                onChange={(e) => setTypeForm((v) => ({ ...v, description: e.target.value }))}
                placeholder="E.g., private pool, ocean view"
              />
            </div>
            <div className="col-md-4 d-flex align-items-center gap-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="type-active"
                checked={typeForm.is_active}
                onChange={(e) => setTypeForm((v) => ({ ...v, is_active: e.target.checked }))}
              />
              <label htmlFor="type-active" className="form-check-label text-sm">
                Active
              </label>
            </div>
            <div className="col-12 d-flex gap-2">
              <button className="btn btn-primary" disabled={saving} onClick={saveType}>
                {saving && editingTypeId ? 'Updating...' : saving ? 'Saving...' : editingTypeId ? 'Update' : 'Add'}
              </button>
              {editingTypeId && (
                <button className="btn btn-outline-secondary" onClick={resetTypeForm}>
                  Cancel
                </button>
              )}
            </div>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Sort</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && !types.length && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      No property types yet
                    </td>
                  </tr>
                )}
                {types.map((t) => (
                  <tr key={t.id}>
                    <td className="fw-semibold">{t.name}</td>
                    <td className="text-muted small">{categoryMap[String(t.category_id)] || '—'}</td>
                    <td>{t.sort_order}</td>
                    <td>
                      <span className={`badge ${t.is_active ? 'bg-success-subtle text-success' : 'bg-secondary'}`}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          setEditingTypeId(t.id);
                          setTypeForm({
                            name: t.name || '',
                            category_id: t.category_id ? String(t.category_id) : '',
                            description: t.description || '',
                            sort_order: t.sort_order || 0,
                            is_active: t.is_active ?? true,
                          });
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPropertyTypes;
